import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./BrokerageManagementPage.css";

const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "Brokerage Earners";

const asNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatAmount = (v, maxDecimals = 8) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  return Number(n.toFixed(maxDecimals)).toString();
};

const userDisplayName = (user) => {
  const parts = [user?.first_name, user?.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const formatByCurrency = (list) => {
  if (!Array.isArray(list) || !list.length) return "";
  return list
    .map((c) => `${c.currency ?? c.currency_id ?? "—"}: ${c.amount ?? 0} (${c.count ?? 0})`)
    .join("; ");
};

const earnerToExport = (row) => ({
  Name: userDisplayName(row?.user) === "—" ? "" : userDisplayName(row?.user),
  Email: row?.user?.email ?? "",
  Phone: row?.user?.phone ?? "",
  UUID: row?.user?.uuid ?? "",
  "User ID": row?.user?.id ?? "",
  "Total Earned": row?.total_earned ?? 0,
  Transactions: row?.total_transactions ?? 0,
  "By Currency": formatByCurrency(row?.by_currency),
});

const BrokerageEarnersPage = () => {
  const navigate = useNavigate();

  const [earners, setEarners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportToast, setExportToast] = useState(null);

  const fetchEarners = async (page = 1, limit = pagination.limit) => {
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getBrokerageEarners({ page, limit });
      if (res?.success) {
        setEarners(Array.isArray(res?.data) ? res.data : []);
        setPagination((prev) => ({
          ...prev,
          ...(res?.pagination || {}),
          page: res?.pagination?.page || page,
          limit: res?.pagination?.limit ?? limit,
        }));
      } else {
        setError(res?.message || "Failed to fetch brokerage earners.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to fetch brokerage earners.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEarners(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllEarners = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];
    do {
      const res = await AdminService.getBrokerageEarners({ page: currentPage, limit: EXPORT_BATCH });
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch earners for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);
    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    setExportToast(null);
    try {
      const allRows = await fetchAllEarners();
      if (!allRows.length) {
        setExportToast({ type: "error", message: "No brokerage earners to export." });
        setTimeout(() => setExportToast(null), 3000);
        return;
      }

      const exportData = allRows.map(earnerToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `brokerage_earners_${dateStamp}.xlsx`);

      setExportToast({
        type: "success",
        message: `Exported ${allRows.length} row(s) to Excel.`,
      });
      setTimeout(() => setExportToast(null), 4000);
    } catch (err) {
      console.error(err);
      setExportToast({
        type: "error",
        message: err?.message || "Failed to export Excel file.",
      });
      setTimeout(() => setExportToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const stats = useMemo(() => {
    return {
      totalEarners: pagination.total || earners.length,
      totalEarned: earners.reduce((sum, x) => sum + asNum(x?.total_earned), 0),
      totalTransactions: earners.reduce((sum, x) => sum + asNum(x?.total_transactions), 0),
    };
  }, [earners, pagination.total]);

  const page = pagination.page || 1;
  const limit = pagination.limit || 20;
  const total = pagination.total ?? 0;
  const totalPages = pagination.total_pages || 1;
  const pageStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const pageEnd = total === 0 ? 0 : Math.min(page * limit, total);

  const goToDetails = (row) => {
    const id = row?.user?.id;
    if (!id) return;
    navigate(`/dashboard/brokerage/earners/${id}`);
  };

  return (
    <main className="dashboard-main dashboard-subpage brokerage-page">
      <section className="dashboard-hero brokerage-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Brokerage</span>
          </p>
          <h1 className="dashboard-hero__title">Brokerage</h1>
          <p className="dashboard-hero__subtitle">Users earning brokerage commissions and their details</p>
        </div>
        <div className="brokerage-hero__icon">
          <i className="fas fa-hand-holding-usd" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="brokerage-stats">
          <div className="brokerage-stat-card">
            <span className="brokerage-stat-card__value">{stats.totalEarners}</span>
            <span className="brokerage-stat-card__label">Total Earners</span>
          </div>
          <div className="brokerage-stat-card">
            <span className="brokerage-stat-card__value">{formatAmount(stats.totalEarned)}</span>
            <span className="brokerage-stat-card__label">Total Earned (current page)</span>
          </div>
          <div className="brokerage-stat-card">
            <span className="brokerage-stat-card__value">{stats.totalTransactions}</span>
            <span className="brokerage-stat-card__label">Transactions (current page)</span>
          </div>
          <div className="brokerage-stat-card">
            <span className="brokerage-stat-card__value">{totalPages}</span>
            <span className="brokerage-stat-card__label">Total Pages</span>
          </div>
        </div>

        <section className="brokerage-section">
          <div className="brokerage-section__head">
            <h2 className="brokerage-section__title">
              <i className="fas fa-users" /> Brokerage Earners
            </h2>
            <button
              type="button"
              className="brokerage-export-btn"
              onClick={handleExportExcel}
              disabled={exportLoading || loading}
              title="Download all brokerage earners as Excel"
            >
              <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
              {exportLoading ? "Exporting…" : "Download Excel"}
            </button>
          </div>

          {exportToast && (
            <p className={`brokerage-export-toast brokerage-export-toast--${exportToast.type}`} role="status">
              {exportToast.message}
            </p>
          )}

          {loading ? (
            <div className="brokerage-empty"><i className="fas fa-spinner fa-spin" /><p>Loading earners...</p></div>
          ) : error ? (
            <div className="brokerage-empty"><i className="fas fa-exclamation-circle" /><p>{error}</p></div>
          ) : earners.length === 0 ? (
            <div className="brokerage-empty"><i className="fas fa-users" /><p>No brokerage earners found</p></div>
          ) : (
            <>
              <div className="brokerage-table-wrap">
                <table className="brokerage-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>UUID</th>
                      <th>Total Earned</th>
                      <th>Transactions</th>
                      <th>By Currency</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earners.map((row) => (
                      <tr key={row?.user?.id || row?.user?.uuid}>
                        <td>{userDisplayName(row?.user)}</td>
                        <td>{row?.user?.email || "—"}</td>
                        <td>{row?.user?.phone || "—"}</td>
                        <td>{row?.user?.uuid || "—"}</td>
                        <td>{row?.total_earned ?? 0}</td>
                        <td>{row?.total_transactions ?? 0}</td>
                        <td>
                          {Array.isArray(row?.by_currency) && row.by_currency.length ? (
                            <span className="brokerage-currency-pills">
                              {row.by_currency.map((c) => (
                                <span key={`${row?.user?.id}-${c.currency_id || c.currency}`} className="brokerage-currency-pill">
                                  {c.currency}: {c.amount} ({c.count})
                                </span>
                              ))}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <button type="button" className="brokerage-btn-view" onClick={() => goToDetails(row)}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="brokerage-pagination">
                <label className="brokerage-page-limit">
                  <span>Rows per page</span>
                  <select
                    value={limit}
                    onChange={(e) => {
                      const nextLimit = Number(e.target.value) || 20;
                      setPagination((prev) => ({ ...prev, limit: nextLimit, page: 1 }));
                      fetchEarners(1, nextLimit);
                    }}
                    disabled={loading}
                  >
                    {PAGE_LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                <span className="brokerage-pagination__info">
                  {total > 0 ? (
                    <>Showing {pageStart}–{pageEnd} of {total} · Page {page} of {totalPages}</>
                  ) : (
                    <>Page {page} of {totalPages}</>
                  )}
                </span>
                <div className="brokerage-pagination__nav">
                  <button
                    type="button"
                    onClick={() => fetchEarners(page - 1, limit)}
                    disabled={page <= 1 || loading}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchEarners(page + 1, limit)}
                    disabled={page >= totalPages || loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default BrokerageEarnersPage;
